import React, { useEffect } from 'react';
import { AutoConfirmedAlert, Confirm, OverlaySpinner, SidebarModalType } from '@adjust/dashboard-components';

import { User } from '@adjust/dashboard-core/api/types';
import { UserDetailsTimezone } from '@react/Pages/AccountSettingsPage/UserDetails/UserDetails/types';
import { SidebarId } from '@react/constants/sidebars';
import { TraceStatus } from '@redux/types';
import { UserDetailsContent } from './UserDetailsContent';

export interface UserDetailsStateProps {
  currentUser: Nullable<User>;
  user: User;
  isAccountOwner: boolean;
  isLoading: boolean;
  timezones: UserDetailsTimezone[];
  trace: { status: TraceStatus; message: string };
  isAlertOpen: boolean;
  isConfirmOpen: boolean;
  isDisabled: boolean;
}

export interface UserDetailsDispatchProps {
  fetchUserDetails: (params: { userId?: string }) => void;
  resetState: () => void;
  updateUser: (values: Record<string, unknown>) => void;
  closeAlert: () => void;
  showConfirm: () => void;
  closeConfirm: () => void;
  deleteUser: () => void;
}

export interface UserDetailsOwnProps {
  closeSidebar: () => void;
  userId?: string;
}

type Props = UserDetailsStateProps & UserDetailsDispatchProps & UserDetailsOwnProps;

export const UserDetails: React.FC<Props> = props => {
  const {
    isAlertOpen,
    isConfirmOpen,
    isLoading,
    currentUser,
    trace,
    resetState,
    closeSidebar,
    closeAlert,
    closeConfirm,
    fetchUserDetails,
    deleteUser,
    ...otherProps
  } = props;

  useEffect(() => {
    fetchUserDetails({ userId: props.userId });
    return resetState;
  }, [fetchUserDetails, resetState, props.userId]);

  const onAlertConfirm = () => {
    if (trace.status === TraceStatus.Info) {
      closeSidebar();
    } else {
      closeAlert();
    }
  };

  return (
    <>
      {isLoading || !currentUser ? (
        <OverlaySpinner isLoading={isLoading} />
      ) : (
        <UserDetailsContent currentUser={currentUser} {...otherProps} closeSidebar={closeSidebar} />
      )}

      <AutoConfirmedAlert
        parentSidebarId={SidebarId.UserDetails}
        isOpen={isAlertOpen}
        type={trace.status}
        message={trace.message}
        onConfirm={onAlertConfirm}
      />
      <Confirm
        isOpen={isConfirmOpen}
        subtitle={t('users.user-delete.warning')}
        type={SidebarModalType.Error}
        onCancel={closeConfirm}
        onConfirm={deleteUser}
        parentSidebarId={SidebarId.UserDetails}
      />
    </>
  );
};
